import { Command } from "commander";
import chalk from "chalk";
import {
  loadData,
  estimateFeature,
  scoreComplexity,
  scoreComplexityAI,
  mapTaskTypes,
  recommendModel,
  isOllamaAvailable,
  classifyComplexityRuleBased,
  type Feature,
  type AIComplexityResult,
} from "@fathom/core";
import { createRouter } from "@fathom/router";
import type { TaskContext as RouterTaskContext } from "@fathom/router";
import { fatalError, warnAction } from "../ux/errors.js";

export const estimateCommand = new Command("estimate")
  .description("Quick single-feature estimate from a description")
  .argument("<description>", "Feature description")
  .option("-c, --complexity <size>", "Override complexity (S/M/L/XL)")
  .option("--category <cat>", "Feature category", "general")
  .option("--no-ai", "Skip AI complexity scoring, use heuristic only")
  .option("--provider <provider>", "LLM provider for AI scoring (anthropic, openai, ollama, rule-based)")
  .option("--no-route", "Skip router recommendation")
  .action(
    async (
      description: string,
      options: { complexity?: string; category?: string; ai?: boolean; provider?: string; route?: boolean },
    ) => {
      let data;
      try {
        data = loadData();
      } catch (err) {
        fatalError(
          "Cannot load Fathom data",
          (err as Error).message,
          "Run `fathom init` to set up the project",
        );
      }

      // Build a minimal feature
      const feature: Feature = {
        id: "quick-estimate",
        name: description.slice(0, 50),
        category: options.category ?? "general",
        complexity: (options.complexity as Feature["complexity"]) ?? "M",
        description,
        dependencies: [],
        aiTasks: [],
      };

      let complexitySource = "heuristic";
      let rationale: string | undefined;

      if (options.complexity) {
        // Manual override — skip all scoring
        complexitySource = "manual";
      } else if (options.ai !== false) {
        // Provider cascade: explicit flag > cloud keys > Ollama > rule-based
        if (options.provider === "rule-based") {
          // Explicit rule-based — no LLM call
          feature.complexity = classifyComplexityRuleBased(description);
          complexitySource = "rule-based";
        } else if (options.provider) {
          // Explicit provider (anthropic, openai, ollama)
          const aiResult = await scoreComplexityAI(description, {
            provider: options.provider as "anthropic" | "openai" | "ollama",
          });
          if (aiResult) {
            feature.complexity = aiResult.complexity;
            feature.dependencies = aiResult.dependencies;
            feature.aiTasks = aiResult.aiTasks;
            complexitySource = "AI";
            rationale = aiResult.rationale;
          } else {
            feature.complexity = scoreComplexity(feature);
          }
        } else {
          // Auto-detect: ANTHROPIC_API_KEY > OPENAI_API_KEY > Ollama > rule-based
          let resolvedProvider: "anthropic" | "openai" | "ollama" | "rule-based" | undefined;
          if (process.env.ANTHROPIC_API_KEY) {
            resolvedProvider = "anthropic";
          } else if (process.env.OPENAI_API_KEY) {
            resolvedProvider = "openai";
          } else if (await isOllamaAvailable()) {
            resolvedProvider = "ollama";
            console.log(chalk.dim("  (Using local Ollama model)\n"));
          } else {
            resolvedProvider = "rule-based";
            console.log(chalk.dim("  (Using built-in classifier — no API key or Ollama detected)\n"));
          }

          if (resolvedProvider === "rule-based") {
            feature.complexity = classifyComplexityRuleBased(description);
            complexitySource = "rule-based";
          } else {
            const aiResult = await scoreComplexityAI(description, {
              provider: resolvedProvider,
            });
            if (aiResult) {
              feature.complexity = aiResult.complexity;
              feature.dependencies = aiResult.dependencies;
              feature.aiTasks = aiResult.aiTasks;
              complexitySource = "AI";
              rationale = aiResult.rationale;
            } else {
              // AI call failed — fall back to heuristic
              feature.complexity = scoreComplexity(feature);
            }
          }
        }
      } else {
        // --no-ai flag
        feature.complexity = scoreComplexity(feature);
      }

      const taskTypes = mapTaskTypes(feature);
      const estimate = estimateFeature(feature, data);
      const rec = recommendModel(feature.category, data.recommendations);

      // Router recommendation (RTR-01) — skipped with --no-route
      let routerRec: { model: string; reason: string; deprecationWarning?: string } | undefined;
      if (options.route !== false) {
        const router = createRouter();
        const routerTask: RouterTaskContext = {
          description,
          complexity: feature.complexity,
          category: options.category,
        };
        routerRec = router.recommend(routerTask);
      }

      console.log(chalk.bold("\nFathom \u2014 Quick Estimate"));
      console.log(chalk.dim("\u2500".repeat(50)));
      console.log(`  Feature:    ${feature.name}`);
      console.log(
        `  Complexity: ${feature.complexity} ${chalk.dim(`(${complexitySource})`)}`,
      );
      if (rationale) {
        console.log(`  Rationale:  ${chalk.dim(rationale)}`);
      }
      console.log(`  Tasks:      ${taskTypes.join(", ")}`);
      console.log(`  Model:      ${rec.model} (${rec.reason})`);
      if (routerRec) {
        console.log(`  Router pick: ${routerRec.model} ${chalk.dim(`(${routerRec.reason})`)}`);
        if (routerRec.deprecationWarning) {
          console.log(chalk.yellow(`  Warning:    ${routerRec.deprecationWarning}`));
        }
      }
      console.log(chalk.dim("\u2500".repeat(50)));
      console.log(
        `  Base tokens:    ${estimate.baseTokens.total.toLocaleString()}`,
      );
      console.log(
        `  With overhead:  ${estimate.withOverhead.total.toLocaleString()}`,
      );
      console.log(
        chalk.bold(`  Estimated cost: $${estimate.estimatedCost.toFixed(2)}`),
      );
      if (estimate.confidence) {
        const c = estimate.confidence;
        console.log(
          chalk.dim(`  Confidence:     $${c.low.cost.toFixed(2)} – $${c.expected.cost.toFixed(2)} – $${c.high.cost.toFixed(2)}  (low / expected / high)`),
        );
        console.log(
          chalk.dim(`                  ${c.low.tokens.toLocaleString()} – ${c.expected.tokens.toLocaleString()} – ${c.high.tokens.toLocaleString()} tokens`),
        );
      }
      console.log(chalk.dim("\n  Overhead breakdown:"));
      console.log(
        `    Context resend: ${estimate.overheadBreakdown.contextResend.toLocaleString()} tokens`,
      );
      console.log(
        `    Tool calls:     ${estimate.overheadBreakdown.toolCalls.toLocaleString()} tokens`,
      );
      console.log(
        `    Thinking:       ${estimate.overheadBreakdown.thinking.toLocaleString()} tokens`,
      );
      console.log(
        `    Error recovery: ${estimate.overheadBreakdown.errorRecovery.toLocaleString()} tokens`,
      );
      console.log(
        `    Planning:       ${estimate.overheadBreakdown.planning.toLocaleString()} tokens`,
      );
      console.log(
        `    Review:         ${estimate.overheadBreakdown.review.toLocaleString()} tokens\n`,
      );
    },
  );
